import { minBreakpoints } from '@noths/polaris-client-ribbons-base';
import { Placement } from '@noths/polaris-client-ribbons-design-system';
import {
  selectFeatureFlags,
  selectIsFeatureFlagVariation,
} from '@noths/polaris-client-user-configuration';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { actions as crossSellModalActions } from 'src/components/organisms/CrossSellModal';
import {
  getFieldPosition,
  scrollToInvalidField,
} from 'src/components/organisms/Personalisation/utils';
import { MIN_PERSONALISATIONS_OPTIONS_FOR_EXPANDING } from 'src/components/organisms/ProductInfo/constants';
import { actions as ProductInfoActions } from 'src/components/organisms/ProductInfo/modules/slice';
import { navigateToCart } from 'src/components/organisms/ProductInfo/modules/thunks';
import { DisplayCrossSellModal } from 'src/constants/featureFlags';
import { addItemToBasket, updateItem } from 'src/redux/basket';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { requestCrossSellProducts } from 'src/redux/richRelevance';
import { selectProduct } from 'src/redux/selectors';
import { AppThunkDispatch } from 'src/redux/store';
import { ProductOption } from 'src/types';
import {
  selectInvalidCustomPersonalisations,
  selectIsPersonalisationInEditMode,
  selectIsPersonalisationOptionsExpanded,
  selectOutstandingPersonalisations,
  selectTrackedEvents,
} from '../selectors';
import { actions } from '../slice';
import { TrackedEvents } from '../types';

const trackOutstandingSelections = (
  outstandingSelections: ProductOption[],
  productOptions: ProductOption[],
  trackedEvents: TrackedEvents,
  dispatch: AppThunkDispatch,
) => {
  outstandingSelections.forEach((opt) => {
    if (!trackedEvents.error['Add to Cart Error'][opt.name]) {
      const isMultipleOptionSelection = (opt.values ?? []).length > 3;
      const selectionLabel = isMultipleOptionSelection
        ? '3 and more values selection'
        : 'single & binary selection';

      dispatch(
        actions.trackInteractionError({
          field_action: 'Add to Cart Error',
          field_name: opt.name,
          field_position: getFieldPosition(productOptions, opt),
          field_type: opt.type === 'selection' ? selectionLabel : 'input field',
          required_field: opt.required,
        }),
      );
    }
  });
};

export const submitPersonalisationForm = createAsyncThunk<void>(
  'personalisation/submitPersonalisationForm',
  async (_, { getState, dispatch }) => {
    const state = getState() as ReduxApplicationState;
    const outstandingSelections = selectOutstandingPersonalisations(state);
    const invalidCustomSelections = selectInvalidCustomPersonalisations(state);
    const isPersonalisationInEditMode = !!selectIsPersonalisationInEditMode(state);
    const product = selectProduct(state);
    const trackedEvents = selectTrackedEvents(state);
    const isPersonalisationOptionsExpanded = selectIsPersonalisationOptionsExpanded(state);
    const featureFlags = selectFeatureFlags(state);
    const isCrossSellModalFeatureVariant =
      selectIsFeatureFlagVariation(
        state,
        DisplayCrossSellModal.NAME,
        DisplayCrossSellModal.DIRECT_TO_CART_ON_CONTINUE_TO_BASKET,
      ) ||
      selectIsFeatureFlagVariation(
        state,
        DisplayCrossSellModal.NAME,
        DisplayCrossSellModal.DIRECT_TO_CART_ON_ADD_TO_BASKET,
      );

    dispatch(actions.showPersonalisationValidationErrors());

    if (
      outstandingSelections.length > 0 ||
      invalidCustomSelections.length > 0 ||
      !product.isQuantityValid
    ) {
      const hasExpandablePersonalisations =
        !isPersonalisationOptionsExpanded &&
        product.options.length >= MIN_PERSONALISATIONS_OPTIONS_FOR_EXPANDING;

      if (hasExpandablePersonalisations) {
        dispatch(actions.toggleExpanded());
      }

      trackOutstandingSelections(outstandingSelections, product.options, trackedEvents, dispatch);

      scrollToInvalidField();
      return;
    }

    if (isPersonalisationInEditMode) {
      await dispatch(updateItem());
    } else {
      await dispatch(addItemToBasket());
    }

    const isSmallScreen = window.innerWidth < minBreakpoints.M;
    const isTabletOrSmaller = window.innerWidth < minBreakpoints.L;
    const crossSellModalFeatureFlag = featureFlags?.[DisplayCrossSellModal.NAME];
    const isCrossSellModalFeatureFlagSet = !!crossSellModalFeatureFlag;

    if (isSmallScreen && isCrossSellModalFeatureFlagSet) {
      const { products } = state.richRelevance[Placement.CrossSell];

      if (products?.length && isCrossSellModalFeatureVariant) {
        // Optimisation - Open modal if we already have placement products in state.
        dispatch(crossSellModalActions.openModal());

        return;
      }

      try {
        const placement = await dispatch(requestCrossSellProducts()).unwrap();

        if (placement?.products.length && isCrossSellModalFeatureVariant) {
          dispatch(crossSellModalActions.openModal());

          return;
        }
      } catch (e) {
        dispatch(navigateToCart());
      }
    }

    if (!isTabletOrSmaller) {
      dispatch(ProductInfoActions.openCartModal());
      return;
    }

    dispatch(navigateToCart());
  },
);
